import React, {useEffect, useRef, useState} from 'react';
import {quickActionPrompt, quickActionReportId} from '../../utils/helpers/quickAction.helpers';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Alert, Empty} from 'antd';
import {ReadyState} from 'react-use-websocket';
import {AnimatePresence} from 'framer-motion';
import {useAgentChat} from '../AgentChat/useAgentChat';
import {AgentChatMessageList, AgentChatWaitingIndicator, AgentSenderInput} from '../AgentChatShared';
import FullWidthAgentChatEmptyState from './FullWidthAgentChatEmptyState';
import {agentWebsocketFlowConstants} from '../../constants';
import {mainActions} from '../../state/actions/main.actions';
import {agentActions} from '../../state/actions/agent.actions';
import {agentChatHistoryHelpers} from '../../utils/helpers/agentChatHistory.helpers';
import {systemHelpers} from '../../utils/helpers';
import {CloseIcon, PlusCircleIcon} from '../../icons';
import {
  StyledFullWidthAgentChatContainer,
  StyledFullWidthAgentChatHeader,
  StyledFullWidthAgentChatCloseButton,
  StyledFullWidthAgentChatNewChatButton,
  StyledFullWidthAgentChatBody,
  StyledFullWidthAgentChatBodyContent,
  StyledFullWidthAgentChatFooter,
  StyledFullWidthAgentChatWaitingSpace,
  StyledFullWidthAgentChatWaitingSpaceContainer,
  StyledFullWidthAgentChatConfirmation,
} from './StyledFullWidthAgentChat';

const FullWidthAgentChat = () => {
  const [t] = useTranslation(['main', 'agentChat']);
  const dispatch = useDispatch();

  const open = useSelector(state => state.main.isFullWidthAgentChatOpen);
  const pendingMessage = useSelector(state => state.main.pendingFullWidthAgentChatMessage);
  const pendingAttachments = useSelector(state => state.main.pendingFullWidthAgentChatAttachments);
  const pendingReportId = useSelector(state => state.main.pendingFullWidthAgentChatReportId);
  const pendingResumeSessionId = useSelector(state => state.main.pendingFullWidthAgentChatResumeSessionId);
  const [restoring, setRestoring] = useState(false);
  const hasStartedConversation = useSelector(state => state.main.hasFullWidthAgentChatStarted);

  // Flips true the first time the view opens and never resets, so it can be
  // passed as useAgentChat's `open` arg: the session/messages stay alive
  // across hide/show, since the hook only clears its state when `open`
  // transitions to false. Actual visibility is controlled by `open`/CSS.
  const wasEverOpenedRef = useRef(false);
  if (open) wasEverOpenedRef.current = true;
  const hasSession = wasEverOpenedRef.current;

  const handleClose = () => dispatch(mainActions.closeFullWidthAgentChat());

  const {
    socketUrl,
    readyState,
    messages,
    error,
    input,
    setInput,
    loading,
    progress,
    selectedOptions,
    isInputDisabled,
    chatBodyRef,
    fileInputRef,
    senderRef,
    handleSend,
    restoreSession,
    hasConversation,
    startNewChat,
    sendAttachments,
    attachmentDrafts,
    removeAttachmentDraft,
    handleFileChange,
    handleAttachmentsChange,
    handlePlusClick,
    toggleOption,
    submitOptions,
    onSubmit,
    parseMessage,
  } = useAgentChat({
    flow: agentWebsocketFlowConstants.EXPENSE_ANALYSIS,
    open: hasSession,
    onClose: handleClose,
    onUserMessage: () => {
      if (!hasStartedConversation) dispatch(mainActions.markFullWidthAgentChatStarted());
    },
    // Remembered so that, after a page reload, opening the chat shows this conversation again (see openFullWidthAgentChat).
    onAssistantMessage: ({session_id: sessionId}) => agentChatHistoryHelpers.rememberSession({
      companyId: systemHelpers.getAuthDetails().companyId,
      sessionId,
    }),
  });

  const isChatEmpty = messages.length === 0 && !hasStartedConversation && !pendingMessage && !pendingAttachments?.length
    && !pendingResumeSessionId && !restoring;

  // A pending message/attachment(s) only ever comes from a quick action starting a fresh
  // request (see QuickAgentAction), never from resuming, so it always starts a new chat.
  // A chip's report id travels with its message, so a report chip on Overview asks for its report too.
  useEffect(() => {
    if (!open || readyState !== ReadyState.OPEN) return;
    if (!pendingMessage && !pendingAttachments?.length) return;
    if (pendingAttachments?.length) {
      sendAttachments(pendingAttachments, pendingMessage || '', {forceNew: true});
    } else if (pendingMessage) {
      handleSend(pendingMessage, {forceNew: true, reportId: pendingReportId});
    }
    dispatch(mainActions.clearPendingFullWidthAgentChatMessage());
  }, [open, pendingMessage, pendingAttachments, readyState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Any open that asks nothing new, after a page reload, is handed the remembered session (openFullWidthAgentChat): the
  // float button and Resume conversation alike draw its stored messages and carry on in it. Nothing drawable leaves a
  // fresh chat and forgets that session. A failed request leaves a fresh chat too, but keeps the session for the next
  // open, so a dropped connection does not lose the conversation.
  // Read when the stored messages arrive, not when they were asked for: a message sent while they were on their way
  // (the chat closed, then a chip or the card's composer) has started a new conversation, and drawing the old one over
  // it would mix the two — or, when the old history has nothing drawable, forget the new conversation's own session.
  // `hasConversation()` is the chat's own answer and is what decides it: this flag is set from `onUserMessage`, which
  // an attachment-only send never reaches, so on that path the flag alone would still read false.
  const hasStartedConversationRef = useRef(hasStartedConversation);
  hasStartedConversationRef.current = hasStartedConversation;
  useEffect(() => {
    if (!open || !pendingResumeSessionId) return;
    const sessionId = pendingResumeSessionId;
    dispatch(mainActions.clearPendingFullWidthAgentChatMessage());
    setRestoring(true);
    dispatch(agentActions.getSessionMessages({
      flow: agentWebsocketFlowConstants.EXPENSE_ANALYSIS,
      sessionId,
      successCallback: (results) => {
        if (hasStartedConversationRef.current || hasConversation()) {
          setRestoring(false);
          return;
        }
        const restored = agentChatHistoryHelpers.toChatMessages(results);
        if (restored.length) {
          restoreSession({sessionId, messages: restored});
          dispatch(mainActions.markFullWidthAgentChatStarted());
        } else {
          agentChatHistoryHelpers.forgetSession();
        }
        setRestoring(false);
      },
      errorCallback: () => setRestoring(false),
    }));
  }, [open, pendingResumeSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // New chat: leaves this conversation for the empty chat and its chips, and forgets it, so a reload does not bring it back.
  const handleNewChat = () => {
    startNewChat();
    agentChatHistoryHelpers.forgetSession();
    dispatch(mainActions.resetFullWidthAgentChat());
  };

  if (!hasSession) return null;

  return (
    <AnimatePresence>
      {open && (
        <StyledFullWidthAgentChatContainer
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.1, ease: 'easeInOut' }}
        >
          <StyledFullWidthAgentChatHeader>
            {/* Disabled while an answer or the stored conversation is on its way: it would land in the new chat. */}
            {socketUrl && !isChatEmpty && (
              <StyledFullWidthAgentChatNewChatButton
                type='default'
                icon={<PlusCircleIcon />}
                disabled={loading || restoring}
                onClick={handleNewChat}
              >
                {t('agentChat:fullWidth.newChat')}
              </StyledFullWidthAgentChatNewChatButton>
            )}
            <StyledFullWidthAgentChatCloseButton
              type='default'
              icon={<CloseIcon />}
              onClick={handleClose}
            />
          </StyledFullWidthAgentChatHeader>

          {!socketUrl ? (
            <Empty description={t('agentChat:errors.serverIsNotConfigured')} />
          ) : isChatEmpty ? (
            <FullWidthAgentChatEmptyState
              value={input}
              onChange={setInput}
              onSubmit={onSubmit}
              disabled={isInputDisabled}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onPlusClick={handlePlusClick}
              attachmentDrafts={attachmentDrafts}
              onRemoveAttachment={removeAttachmentDraft}
              onAttachmentsChange={handleAttachmentsChange}
              onSelectQuickAction={(action) => handleSend(quickActionPrompt(action), {forceNew: true, reportId: quickActionReportId(action)})}
            />
          ) : (
            <>
              <StyledFullWidthAgentChatBody ref={chatBodyRef} align='start' direction='vertical'>
                <StyledFullWidthAgentChatBodyContent>
                  <AgentChatMessageList
                    messages={messages}
                    parseMessage={parseMessage}
                    onSend={handleSend}
                    selectedOptions={selectedOptions}
                    onToggleOption={toggleOption}
                    onSubmitOptions={submitOptions}
                    Confirmation={StyledFullWidthAgentChatConfirmation}
                  />

                  <StyledFullWidthAgentChatWaitingSpaceContainer>
                    {/* `restoring` too: the stored conversation is on its way and nothing is drawn yet.
                        `progress` is null then (it only ever holds what the agent says it is doing), so the
                        indicator falls back to its own wording — see AgentChatWaitingIndicator. */}
                    {(loading || restoring) && (
                      <AgentChatWaitingIndicator WaitingSpace={StyledFullWidthAgentChatWaitingSpace} text={progress} />
                    )}
                  </StyledFullWidthAgentChatWaitingSpaceContainer>

                  {error && (
                    <Alert type='error' showIcon message={error} />
                  )}
                </StyledFullWidthAgentChatBodyContent>
              </StyledFullWidthAgentChatBody>

              <StyledFullWidthAgentChatFooter direction='vertical' size='small' className='w-100'>
                {/* Nothing is sent while the stored conversation loads: it would replace the message on arrival. */}
                <AgentSenderInput
                  senderRef={senderRef}
                  fileInputRef={fileInputRef}
                  disabled={isInputDisabled || restoring}
                  value={input}
                  onChange={setInput}
                  placeholder={t('agentChat:fullWidth.placeholder')}
                  onSubmit={onSubmit}
                  onFileChange={handleFileChange}
                  onPlusClick={handlePlusClick}
                  attachmentDrafts={attachmentDrafts}
                  onRemoveAttachment={removeAttachmentDraft}
                  onAttachmentsChange={handleAttachmentsChange}
                />
              </StyledFullWidthAgentChatFooter>
            </>
          )}
        </StyledFullWidthAgentChatContainer>
      )}
    </AnimatePresence>
  );
};

export default FullWidthAgentChat;
